var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons/faCircleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { loginApi } from "apis/eduidLogin";
import { EduIDButton } from "components/Common/EduIDButton";
import { Splash } from "components/Common/Splash";
import { useAppSelector } from "eduid-hooks";
import { useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { RecoveryOptions } from "../Common/RecoveryOptions";
import { SecurityKey } from "../Common/SecurityKey";
import { LoginAbortButton } from "./LoginAbortButton";
import { LoginAtServiceInfo } from "./LoginAtServiceInfo";
import { RememberMeCheckbox } from "./NewDevice";
import { securityZoneAction, SecurityZoneInfo } from "./SecurityZoneInfo";
export function MultiFactorAuth() {
    var _a;
    const service_info = useAppSelector((state) => state.login.service_info);
    const authn_options = useAppSelector((state) => state.login.authn_options);
    const ref = useAppSelector((state) => state.login.ref);
    const this_device = useAppSelector((state) => state.login.this_device);
    const has_session = authn_options === null || authn_options === void 0 ? void 0 : authn_options.has_session;
    const [fetchMfaAuth, data] = loginApi.useLazyFetchMfaAuthQuery();
    const recoveryOptions = {
        swedish_eid: authn_options.swedish_eid,
        freja_eid: authn_options.freja_eid,
        eidas: authn_options.eidas,
    };
    const show_suggestion = useMemo(() => {
        if (data.isSuccess) {
            const options = data.data.payload.webauthn_options;
            const keys = (options === null || options === void 0 ? void 0 : options.allowCredentials) || [];
            return keys.length === 1 && !authn_options.swedish_eid && !authn_options.eidas && !authn_options.freja_eid;
        }
        return false;
    }, [
        authn_options.swedish_eid,
        authn_options.eidas,
        authn_options.freja_eid,
        (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.payload.webauthn_options,
        data.isSuccess,
    ]);
    let leadText;
    let headingText;
    if (authn_options.swedish_eid || authn_options.eidas || authn_options.freja_eid) {
        leadText = (_jsx(FormattedMessage, { id: "multiFactorAuth.paragraph", defaultMessage: `If you are unable to use the security key, please select other options below, such as BankID, Freja+, eIDAS or freja eID.`, description: "MFA paragraph with swedish option" }));
    }
    if (securityZoneAction) {
        headingText = (_jsx(FormattedMessage, { id: "multiFactorAuth.heading", defaultMessage: "Re-authentication: with MFA", description: "Security zone MFA heading" }));
    }
    else if (has_session) {
        headingText = (_jsx(FormattedMessage, { id: "multiFactorAuth.mfaHeading", defaultMessage: "Log in: with MFA", description: "Login MFA heading" }));
    }
    else {
        headingText = (_jsx(FormattedMessage, { id: "multiFactorAuth.title", defaultMessage: "Welcome, {username}!", description: "start main title", values: {
                username: _jsx("strong", { children: authn_options.display_name }),
            } }));
    }
    const isLoaded = data.isSuccess;
    const hasMfaOptions = authn_options.swedish_eid || authn_options.webauthn || authn_options.eidas || authn_options.freja_eid;
    useEffect(() => {
        if (ref && data.isUninitialized) {
            // Always call the MFA auth endpoint to get the current MFA state:
            //
            // If webauthn is an available choice for this user, fetch a challenge
            // from the backend if we don't have one already.
            //
            // If returning from external authentication with Sweden Connect, we need
            // to call the MFA endpoint for it to complete.
            fetchMfaAuth({ ref: ref, this_device: this_device });
        }
    }, [fetchMfaAuth, data.isUninitialized, ref, this_device]);
    function getChallenge() {
        return __awaiter(this, void 0, void 0, function* () {
            if (ref) {
                const response = yield fetchMfaAuth({ ref: ref, this_device: this_device });
                if (response.isSuccess) {
                    return response.data.payload.webauthn_options;
                }
            }
        });
    }
    function useCredential(credential) {
        if (ref) {
            fetchMfaAuth({ ref: ref, this_device: this_device, webauthn_response: credential });
        }
    }
    return (_jsxs(_Fragment, { children: [_jsxs("section", { className: "intro", children: [_jsx("h1", { children: headingText }), _jsx("div", { className: "lead", children: _jsx(LoginAtServiceInfo, { service_info: service_info }) }), _jsx(SecurityZoneInfo, {})] }), _jsx(Splash, { showChildren: isLoaded, children: hasMfaOptions ? (_jsxs(_Fragment, { children: [_jsxs("p", { children: [_jsx(FormattedMessage, { id: "multiFactorAuth.mfaParagraph", defaultMessage: `Choose a second method to authenticate yourself, ensuring only you can access your eduID. `, description: "MFA paragraph" }), "\u00A0", leadText] }), _jsxs("div", { className: "options", children: [_jsx(SecurityKey, { disabled: !(authn_options === null || authn_options === void 0 ? void 0 : authn_options.webauthn), setup: getChallenge, onSuccess: useCredential }), show_suggestion && (_jsx("span", { className: "suggestion suggestion-txt", children: _jsx(FormattedMessage, { id: "multiFactorAuth.multiple", description: "multiple key suggestion", defaultMessage: "It is strongly recommended to {strong} security key or passkey to ensure you can still log in to your account if one is lost.", values: {
                                            strong: (_jsx("strong", { children: _jsx(FormattedMessage, { id: "multiFactorAuth.multipleKey", description: "multiple key - strong", defaultMessage: `add more than one` }) })),
                                        } }) })), _jsx(RecoveryOptions, { recoveryAvailable: recoveryOptions })] }), _jsx(RememberMeCheckbox, {})] })) : (_jsx(ExtraSecurityNotAvailable, {})) })] }));
}
function ExtraSecurityNotAvailable() {
    const dashboard_link = useAppSelector((state) => state.config.dashboard_link);
    const goToDashboard = () => {
        if (dashboard_link) {
            document.location.href = dashboard_link;
        }
    };
    return (_jsxs("section", { children: [_jsxs("div", { className: "status-box m-b-md", children: [_jsx("div", { className: "checkbox-wrapper", children: _jsx(FontAwesomeIcon, { icon: faCircleExclamation, className: "disabled" }) }), _jsx("div", { className: "text-wrapper", children: _jsx("p", { children: _jsx(FormattedMessage, { id: "multiFactorAuth.serviceRequires", defaultMessage: `The service you are trying to log in to requires an extra level of security.
                           Unfortunately, your eduID account isn't set up with any of the options available
                           for that.
                     `, description: "Login MFA" }) }) })] }), _jsx("h2", { children: _jsx(FormattedMessage, { id: "multiFactorAuth.optionsHeading", defaultMessage: "Options available in the eduID settings:", description: "Login MFA" }) }), _jsxs("ul", { className: "bullets", children: [_jsx("li", { children: _jsx(FormattedMessage, { id: "multiFactorAuth.addKeyInfo", defaultMessage: `Add a Security Key to your account. This can be a physical USB key or a device such as
                 a smartphone or tablet that supports the WebAuthn standard. Some computers also have built-in
                 fingerprint readers that can be used as a Security Key.`, description: "Login MFA" }) }), _jsx("li", { children: _jsx(FormattedMessage, { id: "multiFactorAuth.verifyInfo", defaultMessage: `Verify your identity using the options applicable to you.`, description: "Login MFA" }) })] }), _jsxs("div", { className: "buttons-center", children: [!securityZoneAction && _jsx(LoginAbortButton, {}), _jsx(EduIDButton, { id: "to-eduid-link", buttonstyle: "link normal-case", onClick: goToDashboard, children: _jsx(FormattedMessage, { id: "common.goToEduid", defaultMessage: "go to eduID", description: "Login MFA link" }) })] })] }));
}
